import styled from 'styled-components';
import { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';
import { buildAttributeGroups } from '@upright/common/model/input/companies/products/ipr/attribute-groups';
import { mergeIprAttributes } from '@upright/common/model/input/companies/products/ipr/merge-ipr-attributes';
import { AttributeGroup } from '../ipr-attribute-groups';

interface InheritedAttributesHintProps {
  /** Attributes cascading in from ancestor segments (PLT-5695) — this product's own edits excluded. */
  inheritedIpr: Partial<IPRAttributes> | undefined;
  /** This product's own merged IPR (auto-detected + manual edits), used to keep only inherited-only values. */
  ownIpr: Partial<IPRAttributes>;
}

/**
 * Read-only hint shown at the top of the Edit-attributes modal listing the attributes this product
 * gets from its parent segment(s). The section editors below only show the product's OWN values, so
 * without this the user has no way to tell an attribute is already inherited (e.g. an "Energy
 * sources" section reads empty while the product effectively has "Renewables" from a parent). The
 * chips are non-editable — inherited values are changed by editing the ancestor that sets them
 * (PLT-5799). Mirrors the "(inherited)" treatment used in the Attributes list column.
 */
export function InheritedAttributesHint({ inheritedIpr, ownIpr }: InheritedAttributesHintProps) {
  if (!inheritedIpr) return null;

  // Resolve the same way the list does, then keep only the values that come purely from ancestors —
  // a value the product also sets itself is already visible in its section editor below.
  const resolvedIpr = mergeIprAttributes(inheritedIpr, ownIpr);
  const inheritedGroups = buildAttributeGroups(resolvedIpr, ownIpr)
    .map(group => ({ ...group, chips: group.chips.filter(chip => chip.inherited) }))
    .filter(group => group.chips.length > 0);

  if (inheritedGroups.length === 0) return null;

  return (
    <Container>
      <Heading>Inherited from parent segment</Heading>
      <Groups>
        {inheritedGroups.map(group => (
          <AttributeGroup key={group.title} title={group.title} chips={group.chips} />
        ))}
      </Groups>
    </Container>
  );
}

const Container = styled.div`
  border: 1px solid var(--borderColor-default);
  border-radius: var(--border-radius__md);
  background-color: var(--bgColor-muted, var(--platform-gray-100));
  padding: var(--space__md);
  margin-bottom: var(--space__lg);
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
`;

const Heading = styled.div`
  font-weight: 600;
  color: var(--fgColor-muted);
  font-size: var(--font-size__sm);
`;

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
`;
